import { Inmate } from '@reliance/types/models/Inmate';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Form as FinalForm } from 'react-final-form';
import api from '../common/api';
import { TextArea } from '../form/TextArea';
import { useAlerts } from '../alert/AlertContext';

type InmateDeviceBlacklistAddProps = {
  inmate: Inmate;
  onBlacklistChange?: () => void;
};

type BlacklistFormValues = {
  notes: string;
};

const InmateDeviceBlacklistAdd: React.FC<InmateDeviceBlacklistAddProps> = (props) => {
  const { inmate, onBlacklistChange } = props;
  const [checking, setChecking] = useState(true);
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [error, setError] = useState<any>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const alerts = useAlerts();

  useEffect(() => {
    const checkBlacklistStatus = async () => {
      setChecking(true);
      setError(null);
      try {
        const response = await api.get<boolean>(`/inmate-device-blacklist/is-blacklisted/${inmate.id}`);
        setIsBlacklisted(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setChecking(false);
      }
    };

    if (inmate.id) {
      checkBlacklistStatus();
    }
  }, [inmate.id]);

  const handleAdd = useCallback(async (values: BlacklistFormValues) => {
    setAdding(true);
    setError(null);
    try {
      await api.post('/inmate-device-blacklist', {
        inmateId: inmate.id,
        notes: values.notes,
      });
      setIsBlacklisted(true);
      alerts.success('Inmate was blacklisted', true);
      if (onBlacklistChange) {
        onBlacklistChange();
      }
    } catch (err) {
      setError(err);
      setIsBlacklisted(false);
      alerts.error('Failed to blacklist inmate', true);
      throw err;
    } finally {
      setAdding(false);
    }
  }, [inmate.id, onBlacklistChange]);

  const handleRemove = useCallback(async () => {
    setRemoving(true);
    setError(null);
    try {
      await api.delete(`/inmate-device-blacklist/by-inmate/${inmate.id}`);
      setIsBlacklisted(false);
      alerts.success('Inmate was removed from blacklist', true);
      if (onBlacklistChange) {
        onBlacklistChange();
      }
    } catch (err) {
      setError(err);
      setIsBlacklisted(true);
      alerts.error('Failed to remove inmate from blacklist', true);
    } finally {
      setRemoving(false);
    }
  }, [inmate.id, onBlacklistChange]);

  if (checking) {
    return (
      <Card>
        <Card.Body>
          <p>Checking blacklist status...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>Blacklist Inmate</Card.Header>
      <Card.Body>
        <p>Blacklisting the inmate will prevent the ability for an inmate with this first name, last name and date of birth from renting any further devices.</p>

        {error && (
          <div className="alert alert-danger">
            An error occurred while processing the blacklist request.
          </div>
        )}

        {!isBlacklisted && (
          <FinalForm
            onSubmit={handleAdd}
            initialValues={{ notes: '' }}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <TextArea
                  name="notes"
                  rows={3}
                  placeholder="notes" />
                <Button
                  type="submit"
                  variant="danger"
                  disabled={submitting || adding}>
                  {adding ? 'Blacklisting...' : 'Blacklist Inmate'}
                </Button>
              </form>
            )} />
        )}
      </Card.Body>

      {!checking && isBlacklisted && (
        <Card.Footer>
          <Button
            variant="secondary"
            onClick={handleRemove}
            disabled={removing}>
            {removing ? 'Removing...' : 'Remove From Blacklist'}
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
};

export default InmateDeviceBlacklistAdd;
